import {
  ComponentPropsWithoutRef,
  forwardRef,
  useRef,
  useEffect,
  MouseEvent,
  KeyboardEvent,
  SyntheticEvent,
  createContext,
  useContext,
  useMemo,
  RefObject,
  useId,
  useCallback,
} from "react";
import cx from "classnames";
import {
  useKeyboardFocusables,
  useMergeRefs,
  useOnClickOutside,
} from "../../hooks";
import { useDialogScrollLock } from "../../hooks/useDialogScrollLock";
import styles from "./Drawer.module.scss";
import { DisableCloseOnEscapeOrClickProps, Size } from "../../types";
import { DrawerHeader } from "./DrawerHeader";
import { DrawerFooter } from "./DrawerFooter";
import { DrawerContent } from "./DrawerContent";
import { DrawerCancelButton } from "./DrawerCancelButton";
import { ToasterContent } from "../Toast/Toaster";
import { ToasterPopover } from "../Toast/ToasterPopover";
import { ToastProvider } from "../Toast/ToastProvider";
import { getActiveElement } from "../../internal";
import { useDialogTransitionStates } from "../../hooks/useDialogTransitionStates";
import { OpenCloseTransitionStateEffects } from "../../hooks/useOpenCloseTransitionStates";

/**
 * Props for the Drawer component
 * @extends ComponentPropsWithoutRef<"dialog">
 * @extends DialogTransitionStateEffects
 * @extends DisableCloseOnEscapeOrClickProps
 */
export type DrawerProps = ComponentPropsWithoutRef<"dialog"> &
  OpenCloseTransitionStateEffects &
  DisableCloseOnEscapeOrClickProps & {
    /**
     * The size of the drawer.
     * @default "medium"
     */
    size?: Extract<Size, "medium" | "large">;
    /**
     * Controls whether the drawer is open or closed.
     * @default false
     */
    open?: boolean;
    /**
     * Callback function called after the drawer is animated closed.
     */
    onClose?: () => void;
    /**
     * Callback function called when the drawer opens.
     * @deprecated Use onOpenAnimationStart or onOpenAnimationComplete instead
     */
    onOpen?: () => void;
    /**
     * Callback when clicking outside the drawer
     */
    onClickOutside?: (e: globalThis.MouseEvent) => void;
    /**
     * Enables scroll chaining behavior.
     * @link https://developer.mozilla.org/en-US/docs/Glossary/Scroll_chaining
     */
    enableScrollChaining?: boolean;
  };

/**
 * Context props for the Drawer component
 */
export type DrawerContextProps = {
  /**
   * Function to close the drawer.
   */
  close: (e?: MouseEvent<HTMLElement>) => void;
  /**
   * Reference to the drawer dialog element.
   */
  ref?: RefObject<HTMLDialogElement>;
};

export const DrawerContext = createContext<DrawerContextProps | null>(null);

export const DrawerElement = forwardRef<HTMLDialogElement, DrawerProps>(
  (props, ref) => {
    const {
      className,
      open = false,
      children,
      onOpen,
      onClose,
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars --
        onCancel is destructured so that it is not passed to the dialog in ...rest
      */
      onCancel,
      onKeyDown,
      disableCloseOnEscape = false,
      onClickOutside,
      disableCloseOnClickOutside = false,
      size = "medium",
      enableScrollChaining,
      onOpenAnimationStart,
      onOpenAnimationComplete,
      onCloseAnimationStart,
      onCloseAnimationComplete,
      ...rest
    } = props;

    const elRef = useRef<HTMLDialogElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const toasterRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs([elRef, ref]);
    const id = useId();

    const { state, isOpenOrOpening } = useDialogTransitionStates(elRef, open, {
      onOpenAnimationStart: useCallback(() => {
        onOpenAnimationStart?.();
      }, [onOpenAnimationStart]),
      onOpenAnimationComplete: useCallback(() => {
        onOpenAnimationComplete?.();
        onOpen?.(); // DEPRECATED
      }, [onOpen, onOpenAnimationComplete]),
      onCloseAnimationStart: useCallback(() => {
        onCloseAnimationStart?.();
      }, [onCloseAnimationStart]),
      onCloseAnimationComplete: useCallback(() => {
        onCloseAnimationComplete?.();
      }, [onCloseAnimationComplete]),
    });

    const classNames = cx([styles.drawer], className, {
      [styles.remove]: state === "closing",
      [styles.large]: size === "large",
    });

    const { focusables } = useKeyboardFocusables(contentRef, {
      observeChange: false,
    });
    const firstFocusable = focusables?.[0];
    const lastFocusable = focusables?.[focusables.length - 1];

    useDialogScrollLock({
      open: isOpenOrOpening,
      id,
      enableScrollChaining,
      elementRef: elRef,
    });

    useEffect(() => {
      if (!isOpenOrOpening) return;
      if (!focusables) return;

      const activeElement = getActiveElement(document);
      if (!activeElement) return;

      if (
        activeElement.dataset.anv !== "drawer-header-close-button" &&
        (focusables.includes(activeElement) ||
          activeElement.tagName === "DIALOG")
      ) {
        return;
      }

      if (
        focusables[0]?.dataset.anv === "drawer-header-close-button" &&
        focusables[1]
      ) {
        focusables[1].focus();
      } else {
        focusables[0]?.focus();
      }
    }, [isOpenOrOpening, focusables]);

    const onCancelHandler = (e: SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault();

      if (disableCloseOnEscape) return;
      onClose?.();
    };

    useOnClickOutside({
      onOutsidePress: (e) => {
        if (!open) return;
        onClickOutside?.(e);
      },
      targets: [
        contentRef.current as HTMLElement,
        toasterRef.current as HTMLElement,
      ],
      onClose: () => {
        if (disableCloseOnClickOutside || !open) return;
        onClose?.();
      },
    });

    const onKeyDownHandler = (e: KeyboardEvent<HTMLDialogElement>) => {
      onKeyDown?.(e);
      if (e.code === "Tab") {
        if (e.shiftKey && e.target === firstFocusable) {
          e.preventDefault();
          (lastFocusable as HTMLElement)?.focus();
          return;
        }
        if (e.target === lastFocusable && !e.shiftKey) {
          e.preventDefault();
          (firstFocusable as HTMLElement)?.focus();
          return;
        }
      }
    };

    const contextValue = useMemo(() => {
      return { close: () => onClose?.() };
    }, [onClose]);

    return (
      // dialog element does seem to be interactive on MDN guideline
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <dialog
        className={classNames}
        data-anv="drawer"
        data-anv-scroll-chaining={enableScrollChaining}
        data-anv-transition-state={state}
        ref={combinedRef}
        onCancel={onCancelHandler}
        onKeyDown={onKeyDownHandler}
        {...rest}
      >
        <ToastProvider isDialogToaster={isOpenOrOpening}>
          <ToasterPopover>
            <ToasterContent id="drawer-toaster" ref={toasterRef} />
          </ToasterPopover>
        </ToastProvider>
        <DrawerContext.Provider value={contextValue}>
          <section
            ref={contentRef}
            className={styles.container}
            data-open={isOpenOrOpening}
          >
            {children}
          </section>
        </DrawerContext.Provider>
      </dialog>
    );
  },
);

/**
 * Hook to access the drawer context.
 *
 * @returns The drawer context containing close function and ref
 * @throws Error if used outside of a DrawerContextProvider
 */
export function useDrawerContext() {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error(
      "useDrawerContext must be used within a DrawerContextProvider",
    );
  }
  return context;
}

DrawerElement.displayName = "Drawer";

/**
 * A slide-out drawer component that provides a modal-like experience from the side of the screen.
 *
 * Features:
 * - Slide-in animation from the side of the screen
 * - Keyboard navigation with focus trapping
 * - Escape key and click outside to close
 * - Configurable size variants (medium, large)
 * - Built-in toast support for notifications
 * - Automatic scroll locking when open
 * - Accessible with proper ARIA attributes
 * - Compound component structure with Header, Footer, Content, and CancelButton
 * - Transition state callbacks for animation control
 * - Optional scroll chaining support
 *
 * @example
 * <Drawer open={isOpen} onClose={handleClose} size="medium">
 *   <Drawer.Header>Settings</Drawer.Header>
 *   <Drawer.Content>Drawer content goes here</Drawer.Content>
 *   <Drawer.Footer>
 *     <Button onClick={handleSave}>Save</Button>
 *     <Drawer.CancelButton>Cancel</Drawer.CancelButton>
 *   </Drawer.Footer>
 * </Drawer>
 */
export const Drawer = Object.assign(DrawerElement, {
  /**
   * Header component for the drawer that displays a title and close button.
   *
   * Features:
   * - Displays the drawer title as a headline
   * - Includes an accessible close button
   * - Sticky positioning at the top of the drawer
   * - Proper semantic HTML structure
   * - Accessible with proper ARIA attributes
   * - Consistent styling with the design system
   * - Automatic close functionality
   *
   * @example
   * <Drawer.Header>Settings</Drawer.Header>
   */
  Header: DrawerHeader,
  /**
   * Footer component for the drawer that displays action buttons and controls.
   *
   * Features:
   * - Flexible layout for action buttons and controls
   * - Optional sticky positioning at the bottom
   * - Proper semantic HTML structure
   * - Accessible with proper ARIA attributes
   * - Consistent styling with the design system
   * - Supports all standard footer element props
   * - Responsive design that adapts to content
   *
   * @example
   * <Drawer.Footer sticky>
   *   <Button onClick={handleSave}>Save</Button>
   *   <Drawer.CancelButton>Cancel</Drawer.CancelButton>
   * </Drawer.Footer>
   */
  Footer: DrawerFooter,
  /**
   * Content container for the drawer that holds the main content area.
   *
   * Features:
   * - Flexible content area with proper spacing
   * - Scrollable content when needed
   * - Proper semantic HTML structure
   * - Accessible with proper ARIA attributes
   * - Consistent styling with the design system
   * - Supports all standard div element props
   * - Responsive design that adapts to content
   * - Proper focus management
   *
   * @example
   * <Drawer.Content>
   *   <p>This is the main content of the drawer.</p>
   *   <p>It can contain any type of content.</p>
   * </Drawer.Content>
   */
  Content: DrawerContent,
  /**
   * A button component that automatically closes the drawer when clicked.
   *
   * Features:
   * - Automatically closes the drawer when clicked
   * - Extends Button component functionality
   * - Accessible with proper ARIA attributes
   * - Consistent styling with the design system
   * - Supports all standard button props
   * - Proper focus management
   * - Can be used anywhere within the drawer context
   *
   * @example
   * <Drawer.CancelButton appearance="ghost" size="small">
   *   Cancel
   * </Drawer.CancelButton>
   */
  CancelButton: DrawerCancelButton,
});
